/**
 * Trial request public page — visitor lands here after running a lite
 * assessment. Spec §11.3.
 *
 * URL shape: `/trial/request?ls=<lite_session_id>` (the `ls` param is
 * optional but expected when arriving from the "Unlock the full
 * assessment" CTA at the foot of a lite-assessment result page).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, ArrowRight, Lock } from "lucide-react";
import InfoTooltip from "../components/admin/InfoTooltip";
import { trackEvent, EVENTS } from "../lib/analytics";
import { SUB_BRANDS } from "../data/subBrands";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SECTORS = [
  "Financial Services", "Public Sector", "Telecom", "Manufacturing",
  "Retail & Consumer", "Energy & Utilities", "Healthcare", "Technology",
  "Professional Services", "Other",
];

const SIZE_BANDS = ["1-50", "51-200", "201-1000", "1000+"];

const COUNTRIES = [
  { code: "GB", label: "United Kingdom" },
  { code: "IE", label: "Ireland" },
  { code: "US", label: "United States" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "NL", label: "Netherlands" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "SG", label: "Singapore" },
  { code: "AU", label: "Australia" },
  { code: "OT", label: "Other" },
];

export default function TrialRequest() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const liteSessionId = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("ls") || (typeof window !== "undefined"
      ? window.localStorage.getItem("ekselens.lite_session_id")
      : null);
  }, [search]);

  const [form, setForm] = useState({
    full_name: "",
    business_email: "",
    company: "",
    job_title: "",
    sector: "Financial Services",
    company_size_band: "51-200",
    country: "GB",
    framework_of_interest: "PMO360",
    consent: false,
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    document.title = "Unlock the full assessment — Ekselens Consulting";
  }, []);

  const update = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please give consent to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/trials/request`, {
        ...form,
        lite_session_id: liteSessionId || null,
      });
      setSubmitted({ id: r.data?.id, hours: r.data?.estimated_review_hours || 24 });
      trackEvent(EVENTS.TRIAL_REQUEST, { framework: form.framework_of_interest || "unspecified" });
      toast.success("Request received — we'll be in touch.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(typeof detail === "string" ? detail : "Too many requests — please try again later.");
      } else if (status === 422) {
        toast.error(typeof detail === "string" ? detail : "Please check the form and try again.");
      } else {
        toast.error("Couldn't submit your request — please try again shortly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="bg-white" data-testid="trial-request-confirmed">
        <div className="bg-[#0a0a0a] text-white">
          <div className="ek-container py-20 max-w-3xl">
            <div className="ek-eyebrow text-[#A179FA] mb-4">Trial request</div>
            <h1 className="ek-heading text-3xl md:text-5xl leading-[1.05]">Thanks — we've got your request.</h1>
            <p className="text-zinc-300 text-[15px] mt-6 max-w-xl">
              One of the partners personally reviews each trial request. You'll get an
              email within roughly {submitted.hours} business hours. When approved, we'll
              send a one-click magic-link that signs you straight in for seven days.
            </p>
            <div className="mt-10 inline-flex items-center gap-2 text-[13px] tracking-[0.16em] uppercase text-[#A179FA]">
              <CheckCircle2 size={16} /> Reference {submitted.id}
            </div>
          </div>
        </div>
        <div className="ek-container py-14">
          <button
            type="button"
            onClick={() => navigate("/insights")}
            className="ek-btn ek-btn-outline text-[12px] tracking-[0.16em] uppercase inline-flex items-center gap-2"
            data-testid="trial-after-submit-cta"
          >
            Read insights while you wait <ArrowRight size={14} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white" data-testid="trial-request-page">
      <div className="bg-[#0a0a0a] text-white">
        <div className="ek-container py-16 max-w-3xl">
          <div className="ek-eyebrow text-[#A179FA] mb-4 inline-flex items-center gap-2">
            <Lock size={13} /> Unlock the full assessment
          </div>
          <h1 className="ek-heading text-3xl md:text-5xl leading-[1.05]">
            Ready to put your lite scores under the partner-grade lens?
          </h1>
          <p className="text-zinc-300 text-[15px] mt-6 max-w-2xl">
            The full EKSELENS360 engagement runs the indicator deep-dive, evidence
            reconciliation, peer-benchmark band and tailored CSI moves you saw locked in the
            lite assessment. Every trial is partner-vetted — tell us a little about you and
            we'll be in touch.
          </p>
          {liteSessionId && (
            <div className="mt-6 text-[12px] tracking-[0.14em] uppercase text-zinc-500" data-testid="trial-lite-linked">
              ✓ Linked to your lite session
            </div>
          )}
        </div>
      </div>

      <div className="ek-container py-14 max-w-3xl">
        <form
          onSubmit={submit}
          className="space-y-6"
          data-testid="trial-request-form"
          autoComplete="off"
        >
          {/* Honeypot — visually hidden but bots fill it. */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label>
              Leave this field empty:
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update("website")}
                data-testid="trial-honeypot"
              />
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input
                type="text" required minLength={2}
                value={form.full_name} onChange={update("full_name")}
                className={inputCls}
                data-testid="trial-full-name"
              />
            </Field>
            <Field label="Business email" required info="Please use your work email — trials are reviewed by a partner and approval is sent to this address as a 7-day magic-link. Personal addresses (gmail, outlook) may slow review.">
              <input
                type="email" required
                value={form.business_email} onChange={update("business_email")}
                className={inputCls}
                data-testid="trial-business-email"
              />
            </Field>
            <Field label="Company" required>
              <input
                type="text" required minLength={2}
                value={form.company} onChange={update("company")}
                className={inputCls}
                data-testid="trial-company"
              />
            </Field>
            <Field label="Job title" required info="Your role helps us route the trial to the right partner and calibrate results to your remit (e.g. CIO, Transformation Director, PMO Lead).">
              <input
                type="text" required minLength={2}
                value={form.job_title} onChange={update("job_title")}
                className={inputCls}
                data-testid="trial-job-title"
              />
            </Field>
            <Field label="Sector" required info="Your industry sector. The EKSELENS360 engine calibrates its maturity benchmarks by sector, so this materially shapes your scoring and peer comparison.">
              <select
                value={form.sector} onChange={update("sector")}
                className={inputCls}
                data-testid="trial-sector"
              >
                {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Company size (employees)" required info="Approximate headcount band. We use this to benchmark your results against peers of a similar scale.">
              <select
                value={form.company_size_band} onChange={update("company_size_band")}
                className={inputCls}
                data-testid="trial-size"
              >
                {SIZE_BANDS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Country" required info="Where your organisation is based. We use this for data-residency handling and to apply the right regional regulatory lens to your results.">
              <select
                value={form.country} onChange={update("country")}
                className={inputCls}
                data-testid="trial-country"
              >
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Framework of interest" required info="The EKSELENS360 sub-brand you'd like to assess first. You can run others during your trial — this just sets your starting point.">
              <select
                value={form.framework_of_interest} onChange={update("framework_of_interest")}
                className={inputCls}
                data-testid="trial-framework"
              >
                <option value="" disabled>Select a sub-brand…</option>
                {Object.values(SUB_BRANDS).map((sb) => (
                  <option key={sb.code} value={sb.code}>{sb.code} — {sb.tagline}</option>
                ))}
              </select>
            </Field>
          </div>

          <label className="flex items-start gap-3 text-[13px] text-zinc-700 cursor-pointer" data-testid="trial-consent-label">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={update("consent")}
              className="mt-1 w-4 h-4 accent-[#7737F6]"
              data-testid="trial-consent"
              required
            />
            <span>
              I consent to Ekselens Consulting using the details above to evaluate this
              trial request and to contact me about EKSELENS360. We will not share my
              details with any third party. See our{" "}
              <a href="/privacy" className="text-[#7737F6] hover:underline">privacy policy</a>.
            </span>
          </label>

          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="text-[11.5px] text-zinc-500 max-w-md">
              Partner review typically takes 24 business hours. Approved trials get a
              7-day magic-link to the full assessment.
            </p>
            <button
              type="submit"
              disabled={submitting || !form.consent}
              className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.16em] uppercase px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="trial-submit"
            >
              {submitting ? "Submitting…" : (<>Request trial access <ArrowRight size={14} /></>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const inputCls =
  "mt-2 w-full border border-zinc-300 px-3 py-2 text-[16px] focus:outline-none focus:border-[#7737F6] bg-white text-black";

function Field({ label, required, info, children }) {
  return (
    <label className="block text-[12px] tracking-[0.14em] uppercase text-zinc-500">
      {label}{required && <span className="text-[#7737F6]">*</span>}
      {info && <InfoTooltip testid={`trial-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-info`}>{info}</InfoTooltip>}
      {children}
    </label>
  );
}
